import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Wallet, 
  CreditCard, 
  History, 
  Receipt, 
  Plus, 
  Search,
  CheckCircle,
  AlertCircle,
  XCircle,
  Clock,
  DollarSign,
  Users,
  TrendingUp
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

function PaymentsPage() {
  const { t, isRTL } = useLanguage();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [charges, setCharges] = useState([]);
  const [gateways, setGateways] = useState({ stripe: { active: true }, paypal: { active: false }, coinpayup: { active: false } });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchQuery, setSearchQuery] = useState('');
  
  // Add Balance Dialog
  const [addBalanceOpen, setAddBalanceOpen] = useState(false);
  const [addAmount, setAddAmount] = useState('');
  const [selectedGateway, setSelectedGateway] = useState('stripe');
  const [processing, setProcessing] = useState(false);
  
  const showMessage = useCallback((type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  }, []);
  
  const fetchData = useCallback(async () => {
    try {
      const [balanceRes, historyRes, chargesRes, gatewaysRes] = await Promise.all([
        axios.get(`${API}/admin/payment/balance`, { withCredentials: true }),
        axios.get(`${API}/admin/payment/history`, { withCredentials: true }),
        axios.get(`${API}/admin/payment/charges`, { withCredentials: true }),
        axios.get(`${API}/admin/payment/gateways`, { withCredentials: true })
      ]);
      
      setBalance(balanceRes.data);
      setTransactions(historyRes.data.transactions || []);
      setCharges(chargesRes.data.charges || []);
      setGateways(gatewaysRes.data);
    } catch (error) {
      console.error('Failed to fetch payment data:', error);
      showMessage('error', 'Failed to load payment information');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);
  
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      showMessage('error', 'Payment status check timed out. Please refresh the page.');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/admin/payment/checkout-status/${sessionId}`, {
        withCredentials: true
      });
      
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        showMessage('success', 'Payment successful! Your balance has been updated.');
        fetchData();
        return;
      } else if (data.status === 'expired' || data.payment_status === 'expired') {
        showMessage('error', data.message || 'Payment session expired. Please try again.');
        fetchData(); // Refresh to show updated status
        return;
      } else if (data.status === 'canceled' || data.payment_status === 'canceled') {
        showMessage('error', data.message || 'Payment was canceled. Please start a new payment.');
        fetchData(); // Refresh to show updated status
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      const errorMsg = error.response?.data?.detail || 'Error checking payment status. Please refresh the page.';
      showMessage('error', errorMsg);
      fetchData(); // Refresh to get latest data
    }
  }, [showMessage, fetchData]);
  
  useEffect(() => {
    fetchData();
    
    // Check for payment result in URL
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    const isCoinPayUp = searchParams.get('coinpayup');
    const sessionId = searchParams.get('session_id');
    const isPaypal = searchParams.get('paypal');
    const paypalToken = searchParams.get('token');
    const txnId = searchParams.get('txn_id');
    // CoinPayUp may also append their own invoice id params
    const cpInvoiceId = searchParams.get('invoice_id') || searchParams.get('id');
    
    if (success && sessionId) {
      // Stripe payment success
      pollPaymentStatus(sessionId);
    } else if (success && isPaypal && paypalToken) {
      // PayPal payment success - capture the payment
      capturePayPalPayment(paypalToken);
    } else if (isCoinPayUp && (success || txnId || cpInvoiceId)) {
      // CoinPayUp redirect — check by txn_id, or fall back to most recent pending
      checkCoinPayUpStatus(txnId, cpInvoiceId);
    } else if (cancelled) {
      showMessage('error', 'Payment was cancelled');
    }
  }, [searchParams, fetchData, pollPaymentStatus, showMessage]);
  
  const capturePayPalPayment = async (orderId) => {
    try {
      const response = await axios.post(`${API}/admin/payment/paypal/capture/${orderId}`, {}, {
        withCredentials: true
      });
      
      if (response.data.status === 'completed' || response.data.status === 'already_completed') {
        showMessage('success', response.data.message || 'Payment successful! Your balance has been updated.');
        fetchData();
      } else {
        showMessage('error', 'Payment could not be completed. Please try again.');
      }
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to capture payment');
    }
  };
  
  const checkCoinPayUpStatus = async (txnId, cpInvoiceId) => {
    try {
      const histRes = await axios.get(`${API}/admin/payment/history`, { withCredentials: true });
      const allTxns = histRes.data.transactions || [];

      let txn = null;
      if (txnId) {
        // Find by our transaction_id
        txn = allTxns.find(t => t.transaction_id === txnId);
      }
      if (!txn && cpInvoiceId) {
        // Find by CoinPayUp's own invoice id
        txn = allTxns.find(t => t.coinpayup_invoice_id === cpInvoiceId);
      }
      if (!txn) {
        // Fall back to most recent pending CoinPayUp transaction
        txn = allTxns
          .filter(t => t.payment_method === 'coinpayup' && t.payment_status === 'pending' && t.coinpayup_invoice_id)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
      }

      if (!txn || !txn.coinpayup_invoice_id) {
        showMessage('info', 'Crypto payment initiated. Status will update automatically via webhook.');
        return;
      }
      
      const response = await axios.get(`${API}/admin/payment/coinpayup/status/${txn.coinpayup_invoice_id}`, {
        withCredentials: true
      });
      
      if (response.data.status === 'completed') {
        showMessage('success', response.data.message || 'Crypto payment confirmed! Balance updated.');
        fetchData();
      } else {
        showMessage('info', `Crypto payment status: ${response.data.status || 'pending'}. It may take a few minutes to confirm on blockchain.`);
      }
    } catch (error) {
      showMessage('info', 'Crypto payment initiated. Status will update once confirmed on the blockchain.');
    }
  };
  
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [converting, setConverting] = useState(false);
  
  const checkConversion = async (amount) => {
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      setConvertedAmount(null);
      return;
    }
    
    const currency = balance?.currency || 'USD';
    if (currency === 'USD') {
      setConvertedAmount(null);
      return;
    }
    
    setConverting(true);
    try {
      const response = await axios.get(`${API}/admin/payment/convert-currency`, {
        params: {
          amount: parseFloat(amount),
          from_currency: currency,
          to_currency: 'USD'
        },
        withCredentials: true
      });
      setConvertedAmount(response.data);
    } catch (error) {
      console.error('Conversion error:', error);
      setConvertedAmount(null);
    } finally {
      setConverting(false);
    }
  };
  
  // Debounce conversion check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (addAmount) {
        checkConversion(addAmount);
      } else {
        setConvertedAmount(null);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [addAmount, balance?.currency]);
  
  const handleAddBalance = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(addAmount);
    if (isNaN(amount) || amount <= 0) {
      showMessage('error', 'Please enter a valid amount');
      return;
    }
    
    setProcessing(true);
    
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/admin/payment/add-balance`, {
        amount: amount,
        payment_method: selectedGateway,
        origin_url: originUrl
      }, { withCredentials: true });
      
      // Redirect to checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to initiate payment');
      setProcessing(false);
    }
  };
  
  const handleSearch = async () => {
    try {
      const response = await axios.get(`${API}/admin/payment/history`, {
        params: { search: searchQuery },
        withCredentials: true
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      completed: { color: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100', icon: CheckCircle },
      pending: { color: 'bg-amber-50 text-amber-800 ring-1 ring-amber-100', icon: Clock },
      failed: { color: 'bg-rose-50 text-rose-700 ring-1 ring-rose-100', icon: XCircle },
      expired: { color: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200', icon: XCircle },
      canceled: { color: 'bg-orange-50 text-orange-700 ring-1 ring-orange-100', icon: XCircle }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${badge.color}`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };
  
  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
      </div>
    );
  }

  const kpiCards = [
    {
      title: 'Current balance',
      value: `${balance?.currency || ''} ${balance?.balance?.toFixed(2) || '0.00'}`,
      sub: balance?.is_low_balance ? 'Low — add funds' : 'Available credit',
      icon: Wallet,
      soft: balance?.is_low_balance ? 'bg-rose-50 text-rose-600' : 'bg-indigo-50 text-indigo-600',
      accent: balance?.is_low_balance ? 'from-rose-500 to-orange-500' : 'from-indigo-500 to-purple-500',
      warn: !!balance?.is_low_balance,
    },
    {
      title: 'Staff count',
      value: balance?.staff_count || 0,
      sub: `Free ${balance?.free_staff_limit || 0} · Paid ${balance?.paid_staff || 0}`,
      icon: Users,
      soft: 'bg-sky-50 text-sky-600',
      accent: 'from-sky-500 to-pink-500',
    },
    {
      title: 'Monthly charge',
      value: `${balance?.currency || ''} ${balance?.monthly_charge?.toFixed(2) || '0.00'}`,
      sub: `Tier: ${(balance?.pricing_tier || 'free').replace('_', ' ')}`,
      icon: DollarSign,
      soft: 'bg-purple-50 text-purple-600',
      accent: 'from-purple-500 to-pink-500',
    },
    {
      title: 'Per staff',
      value: `${balance?.currency || ''} ${balance?.price_per_staff?.toFixed(2) || '0.00'}`,
      sub: '/staff / month',
      icon: TrendingUp,
      soft: 'bg-amber-50 text-amber-700',
      accent: 'from-amber-500 to-orange-500',
    },
  ];
  
  return (
    <div className="relative pb-8" data-testid="payments-page">
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

      <div className="relative space-y-5 md:space-y-6">
        <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 text-white sm:px-6 sm:py-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">{t('payments.title')}</h2>
                <p className="mt-1.5 text-sm text-indigo-50/90">
                  Manage billing, top-ups, and charge history
                </p>
              </div>
              <Button
                onClick={() => setAddBalanceOpen(true)}
                className="h-11 rounded-2xl bg-white px-5 font-semibold text-indigo-700 shadow-md hover:bg-indigo-50"
                data-testid="add-balance-button"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Balance
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-3 sm:px-6">
            <p className="text-sm font-medium text-slate-600">
              {transactions.length} payment{transactions.length === 1 ? '' : 's'} · {charges.length} charge{charges.length === 1 ? '' : 's'}
            </p>
            {balance?.is_low_balance && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-700 ring-1 ring-rose-100">
                <AlertCircle className="h-3.5 w-3.5" />
                Low balance
              </span>
            )}
          </div>
        </section>
      
        {message.text && (
          <div className={`att-fade-up flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium ring-1 ${
            message.type === 'success' ? 'bg-indigo-50 text-indigo-700 ring-indigo-200' : ''
          } ${message.type === 'error' ? 'bg-rose-50 text-rose-700 ring-rose-200' : ''} ${
            message.type === 'info' ? 'bg-sky-50 text-sky-700 ring-sky-200' : ''
          }`}>
            {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            {message.text}
          </div>
        )}
      
        <section className="att-fade-up grid grid-cols-2 gap-3 lg:grid-cols-4 md:gap-4" style={{ animationDelay: '60ms' }}>
          {kpiCards.map((k) => (
            <div
              key={k.title}
              className={`group relative overflow-hidden rounded-2xl border bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-lg ${
                k.warn ? 'border-rose-200/80' : 'border-slate-200/80'
              }`}
            >
              <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br ${k.accent} opacity-10 blur-xl`} />
              <div className="relative flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{k.title}</p>
                  <p className={`mt-2 truncate text-2xl font-bold tabular-nums tracking-tight sm:text-3xl ${
                    k.warn ? 'text-rose-600' : 'text-slate-900'
                  }`}>
                    {k.value}
                  </p>
                  <p className={`mt-1 text-xs ${k.warn ? 'font-semibold text-rose-600' : 'text-slate-500'}`}>{k.sub}</p>
                </div>
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${k.soft}`}>
                  <k.icon className="h-5 w-5" />
                </div>
              </div>
            </div>
          ))}
        </section>
      
        <Tabs defaultValue="history" className="att-fade-up w-full" style={{ animationDelay: '120ms' }}>
          <TabsList className="inline-flex h-auto w-full gap-1 rounded-2xl bg-slate-100/90 p-1.5 sm:w-auto">
            <TabsTrigger value="history" className="flex flex-1 items-center justify-center gap-2 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm sm:flex-none sm:px-5">
              <History className="h-4 w-4" />
              Payment History
            </TabsTrigger>
            <TabsTrigger value="charges" className="flex flex-1 items-center justify-center gap-2 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm sm:flex-none sm:px-5">
              <Receipt className="h-4 w-4" />
              Payment Charges
            </TabsTrigger>
          </TabsList>
        
          <TabsContent value="history" className="mt-4">
            <div className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm">
              <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                      <CreditCard className="h-4 w-4" />
                    </span>
                    Payment History
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">Your balance top-up transactions</p>
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1 sm:w-64">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search transactions…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="h-10 rounded-2xl border-slate-200 pl-9"
                      data-testid="search-transactions"
                    />
                  </div>
                  <Button variant="outline" onClick={handleSearch} className="h-10 rounded-2xl border-slate-200">
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80">
                      <TableHead className="font-semibold">Transaction ID</TableHead>
                      <TableHead className="font-semibold">{t('common.date')}</TableHead>
                      <TableHead className="font-semibold">Method</TableHead>
                      <TableHead className="text-right font-semibold">{t('payments.amount')}</TableHead>
                      <TableHead className="font-semibold">{t('common.status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="py-14 text-center text-slate-400">
                          No payment history found. Add balance to see transactions here.
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions.map((txn) => (
                        <TableRow key={txn.transaction_id} data-testid={`txn-row-${txn.transaction_id}`}>
                          <TableCell className="font-mono text-sm text-slate-700">{txn.transaction_id}</TableCell>
                          <TableCell className="text-slate-600">{formatDate(txn.created_at)}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-700 ring-1 ring-sky-100">
                              <CreditCard className="h-3 w-3" />
                              {txn.payment_method?.toUpperCase() || 'STRIPE'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-bold tabular-nums text-indigo-600">
                            +{txn.currency} {txn.amount?.toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap items-center gap-2">
                              {getStatusBadge(txn.payment_status)}
                              {txn.payment_method === 'coinpayup' && txn.payment_status === 'pending' && txn.coinpayup_invoice_id && (
                                <button
                                  data-testid={`check-coinpayup-status-${txn.transaction_id}`}
                                  onClick={async () => {
                                    try {
                                      const res = await axios.get(`${API}/admin/payment/coinpayup/status/${txn.coinpayup_invoice_id}`, { withCredentials: true });
                                      if (res.data.status === 'completed') {
                                        showMessage('success', res.data.message || 'Payment confirmed! Balance updated.');
                                        fetchData();
                                      } else {
                                        showMessage('info', `Status: ${res.data.status}. Payment may still be processing.`);
                                      }
                                    } catch {
                                      showMessage('error', 'Failed to check payment status.');
                                    }
                                  }}
                                  className="whitespace-nowrap rounded-full bg-purple-600 px-2.5 py-0.5 text-xs font-semibold text-white transition hover:bg-purple-700"
                                >
                                  Check Status
                                </button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        
          <TabsContent value="charges" className="mt-4">
            <div className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-5 py-4">
                <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                    <Receipt className="h-4 w-4" />
                  </span>
                  Staff Charges
                </h3>
                <p className="mt-1 text-sm text-slate-500">Monthly and prorated charges for paid staff</p>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80">
                      <TableHead className="font-semibold">Charge ID</TableHead>
                      <TableHead className="font-semibold">Billing Period</TableHead>
                      <TableHead className="font-semibold">Staff Count</TableHead>
                      <TableHead className="font-semibold">Type</TableHead>
                      <TableHead className="text-right font-semibold">{t('payments.amount')}</TableHead>
                      <TableHead className="font-semibold">{t('common.date')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {charges.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="py-14 text-center text-slate-400">
                          No charges found. Charges appear when you exceed the free staff limit.
                        </TableCell>
                      </TableRow>
                    ) : (
                      charges.map((charge) => (
                        <TableRow key={charge.charge_id} data-testid={`charge-row-${charge.charge_id}`}>
                          <TableCell className="font-mono text-sm text-slate-700">{charge.charge_id}</TableCell>
                          <TableCell className="text-slate-600">{charge.billing_period}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-700 ring-1 ring-sky-100">
                              <Users className="h-3 w-3" />
                              {charge.staff_count} staff
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
                              charge.charge_type === 'prorated'
                                ? 'bg-orange-50 text-orange-700 ring-orange-100'
                                : 'bg-purple-50 text-purple-700 ring-purple-100'
                            }`}>
                              {charge.charge_type === 'prorated' ? `Prorated (${charge.days_charged} days)` : 'Monthly'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-bold tabular-nums text-rose-600">
                            −{charge.currency} {charge.amount?.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-slate-600">{formatDate(charge.created_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      
        <Dialog open={addBalanceOpen} onOpenChange={(open) => { if (!processing) setAddBalanceOpen(open); }}>
          <DialogContent className="overflow-hidden rounded-3xl border-0 p-0 shadow-2xl sm:max-w-md">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-6 py-5 text-white">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-white">
                  <Wallet className="h-5 w-5" />
                  Add Balance
                </DialogTitle>
                <DialogDescription className="text-indigo-50/90">
                  Top up your account balance to cover staff charges
                </DialogDescription>
              </DialogHeader>
            </div>
            <form onSubmit={handleAddBalance} className="space-y-4 px-6 py-5">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount ({balance?.currency || 'USD'})</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="amount"
                    type="number"
                    min="1"
                    step="0.01"
                    value={addAmount}
                    onChange={(e) => setAddAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="h-11 rounded-2xl pl-10"
                    required
                    disabled={processing}
                    data-testid="add-balance-amount"
                  />
                </div>
              </div>
            
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={selectedGateway} onValueChange={setSelectedGateway} disabled={processing}>
                  <SelectTrigger className="h-11 rounded-2xl" data-testid="payment-method-select">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    {gateways.stripe?.active && (
                      <SelectItem value="stripe">
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          Stripe (Card)
                        </div>
                      </SelectItem>
                    )}
                    {gateways.paypal?.active && (
                      <SelectItem value="paypal">
                        <div className="flex items-center gap-2">
                          <Wallet className="h-4 w-4" />
                          PayPal
                        </div>
                      </SelectItem>
                    )}
                    {gateways.coinpayup?.active && (
                      <SelectItem value="coinpayup">
                        <div className="flex items-center gap-2">
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M9.5 8h5a2 2 0 010 4h-5m5 0a2 2 0 010 4h-5m0-8v8m0-8V6m0 10v2" />
                          </svg>
                          CoinPayUp (Crypto)
                        </div>
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            
              <div className="space-y-2">
                <Label>Quick Select</Label>
                <div className="grid grid-cols-4 gap-2">
                  {[10, 25, 50, 100].map((amount) => (
                    <Button
                      key={amount}
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={processing}
                      onClick={() => setAddAmount(amount.toString())}
                      className={`h-10 rounded-xl ${
                        addAmount === String(amount)
                          ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200'
                      }`}
                    >
                      {balance?.currency || '$'}{amount}
                    </Button>
                  ))}
                </div>
              </div>
            
              <DialogFooter className="gap-2 border-t border-slate-100 pt-4 sm:space-x-0">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 rounded-2xl"
                  disabled={processing}
                  onClick={() => setAddBalanceOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={processing}
                  className="h-11 rounded-2xl bg-indigo-600 hover:bg-indigo-700"
                  data-testid="confirm-add-balance"
                >
                  {processing ? 'Processing…' : 'Continue to Payment'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default PaymentsPage;
